import type {
	ProjectDTO,
	ProjectStatus,
	UpdateProjectBody,
} from "@zplanem/responses"
import { useOutletContext } from "react-router-dom"

/**
 * Everything the project sub-tabs need from `ProjectDetailLayout`, passed
 * through react-router's `<Outlet context>`. The layout owns the data,
 * permissions and mutations; the Details tab renders the project header
 * (identity + Edit/Archive/status) and is the only real consumer — the
 * Visualizations and Inspirations tabs ignore it.
 */
export interface ProjectDetailContext {
	propertyId: string
	project: ProjectDTO
	isOwner: boolean
	canEdit: boolean
	canSetStatus: boolean
	canManageMembers: boolean
	canProposeCost: boolean
	canMarkPaid: boolean
	canArchive: boolean
	archived: boolean
	/** Edit toggle lives in the Details tab's header; the form in the tab. */
	editing: boolean
	setEditing: (value: boolean) => void
	/** `mutateAsync` handles — the tab awaits them and maps errors locally. */
	onUpdateStatus: (status: ProjectStatus) => Promise<unknown>
	updateStatusPending: boolean
	onSetArchived: (archived: boolean) => Promise<unknown>
	setArchivedPending: boolean
	onUpdateFields: (body: UpdateProjectBody) => Promise<unknown>
	updateFieldsPending: boolean
	onRemoveMember: (userId: string) => Promise<unknown>
	removeMemberPending: boolean
	onOpenAddMember: () => void
	/** Runs a mutation and surfaces failures via the layout's shared error. */
	runAction: (action: Promise<unknown>) => Promise<void>
}

export function useProjectDetailContext(): ProjectDetailContext {
	return useOutletContext<ProjectDetailContext>()
}
